/**
 * 계약서 템플릿 스토어
 * - templates: 등록된 템플릿 목록 (백엔드 연동 전 mock + 사용자 추가본 보관)
 * - importedDraft: 임포트 결과를 편집기로 넘기는 임시 데이터
 *
 * persist 로 localStorage 에 저장 → 새로고침해도 유지
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  ContractField,
  ContractTemplateData,
} from "@/business/contract/contractTemplate";

export interface ContractTemplateListItem {
  contractSeq: number;
  name: string;
  fieldCount: number;
  signedCount: number;
  active: boolean;
  updatedAt: string; // YYYY-MM-DD
  status: "active" | "draft" | "inactive";
  /** 본문/필드까지 들고 있어서 편집기에서 그대로 로드 */
  data: ContractTemplateData;
}

export interface ImportedDraft {
  /** 추출된 본문 — 변수는 [key] 로 감싸진 상태가 좋지만, 미감싼 상태도 허용 */
  body: string;
  fields: ContractField[];
  /** PDF 에서 추출된 TipTap JSON — 폰트·크기·정렬·제목 모두 보존 */
  docJson?: any;
  /** 추정 이름 (파일명에서 확장자 제거) */
  suggestedName: string;
  /** 출처 파일 정보 */
  sourceFile: { name: string; size: number; type: "docx" | "pdf" | "hwpx" | "pptx" };
  /** 통계 */
  stats: { paragraphs: number; tables: number; images: number; accuracy: number };
  /** 임포트 원본 파일 업로드 후 받은 INFO_FILE.FILE_SEQ */
  sourceFileSeq?: number;
}

interface State {
  templates: ContractTemplateListItem[];
  importedDraft: ImportedDraft | null;
}

interface Actions {
  /** 등록된 템플릿 추가 — 새 contractSeq 자동 부여 */
  addTemplate: (data: ContractTemplateData) => ContractTemplateListItem;
  /** 기존 템플릿 갱신 */
  updateTemplate: (contractSeq: number, data: ContractTemplateData) => void;
  /** 삭제 */
  removeTemplate: (contractSeq: number) => void;
  /** 임포트 draft 보관 */
  setImportedDraft: (draft: ImportedDraft | null) => void;
  /** draft 비우기 (편집기에서 한 번 사용 후) */
  clearImportedDraft: () => void;
  /** 단일 조회 */
  getTemplate: (contractSeq: number) => ContractTemplateListItem | undefined;
}

const today = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 백엔드 연동 전 기본 노출용 mock — 첫 진입 시 한 번만 채워짐 */
const SEED_TEMPLATES: ContractTemplateListItem[] = [
  {
    contractSeq: 1,
    name: "위탁 계약서 v3",
    fieldCount: 4,
    signedCount: 234,
    active: true,
    updatedAt: "2026-04-15",
    status: "active",
    data: {
      contractSeq: 1,
      name: "위탁 계약서 v3",
      body: "",
      fields: [],
      active: true,
      type: "CONTRACT_TYPE_CONSIGNMENT",
      autoFirstReservYn: "Y",
    },
  },
  {
    contractSeq: 2,
    name: "미용 동의서 v1",
    fieldCount: 2,
    signedCount: 0,
    active: false,
    updatedAt: "2026-04-29",
    status: "draft",
    data: {
      contractSeq: 2,
      name: "미용 동의서 v1",
      body: "",
      fields: [],
      active: false,
      type: "CONTRACT_TYPE_CUSTOM",
      autoFirstReservYn: "N",
    },
  },
  {
    contractSeq: 3,
    name: "일일 체험 동의서",
    fieldCount: 3,
    signedCount: 89,
    active: true,
    updatedAt: "2026-03-22",
    status: "active",
    data: {
      contractSeq: 3,
      name: "일일 체험 동의서",
      body: "",
      fields: [],
      active: true,
      type: "CONTRACT_TYPE_CUSTOM",
      autoFirstReservYn: "N",
    },
  },
];

export const useContractTemplateStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      templates: SEED_TEMPLATES,
      importedDraft: null,

      addTemplate: (data) => {
        const list = get().templates;
        const nextSeq =
          list.reduce((max, t) => Math.max(max, t.contractSeq), 0) + 1;
        const item: ContractTemplateListItem = {
          contractSeq: nextSeq,
          name: data.name || "이름 없는 템플릿",
          fieldCount: data.fields.length,
          signedCount: 0,
          active: data.active,
          updatedAt: today(),
          status: data.active ? "active" : "draft",
          data: { ...data, contractSeq: nextSeq },
        };
        set({ templates: [item, ...list] });
        return item;
      },

      updateTemplate: (contractSeq, data) => {
        set({
          templates: get().templates.map((t) =>
            t.contractSeq === contractSeq
              ? {
                  ...t,
                  name: data.name || t.name,
                  fieldCount: data.fields.length,
                  active: data.active,
                  status: data.active ? "active" : "draft",
                  updatedAt: today(),
                  data: { ...data, contractSeq },
                }
              : t
          ),
        });
      },

      removeTemplate: (contractSeq) => {
        set({
          templates: get().templates.filter(
            (t) => t.contractSeq !== contractSeq
          ),
        });
      },

      setImportedDraft: (draft) => set({ importedDraft: draft }),
      clearImportedDraft: () => set({ importedDraft: null }),
      getTemplate: (contractSeq) =>
        get().templates.find((t) => t.contractSeq === contractSeq),
    }),
    {
      name: "contract-template-store",
      storage: createJSONStorage(() => localStorage),
      /**
       *  ⚠ importedDraft 는 persist 제외 — 임포트 결과 (docJson) 가 수십 MB 단위로
       *     커질 수 있어 localStorage quota (브라우저당 약 5MB) 를 즉시 초과함.
       *     SPA 내 in-memory zustand 싱글톤이라 import → edit 페이지 이동에서는
       *     그대로 유지되며, 새로고침 시에만 비워짐 (의도된 동작).
       *  templates 만 영속화 — 사용자 등록 템플릿은 백엔드 연동 전까지 보존.
       */
      partialize: (state) => ({ templates: state.templates }),
    }
  )
);
